import {
  AutoAwesome,
  CalendarMonth,
  Description,
  Groups,
  HeadsetMic,
  Telegram,
} from "@mui/icons-material";
import type { SvgIconComponent } from "@mui/icons-material";
import { Box, Button, Card, Divider, Typography } from "@mui/joy";
import { useState } from "react";
import { useNavigate } from "react-router";

import { LandingLayout } from "../LandingLayout/LandingLayout";
import { LandingSection } from "../LandingLayout/LandingSection";
import { PricingFAQ } from "./components/PricingFAQ/PricingFAQ";
import { ScramblePrice } from "./components/ScramblePrice/ScramblePrice";
import {
  CompanyPaymentPeriod,
  type CompanyPaymentPeriodType,
  type SignupAllowedPlan,
} from "~/common/api/validation/auth-schemas";
import { ToggleSwitch } from "~/common/components";
import { FadeIn } from "~/common/components/FadeIn/FadeIn";
import { RoutePaths } from "~/common/constants/route-paths";
import { useSignupPlanStore } from "~/modules/auth/stores/signup-plan-store";

interface PlanFeature {
  icon: SvgIconComponent;
  label: string;
}

interface PricingPlan {
  name: string;
  planKey: SignupAllowedPlan | null; // null for enterprise (not available for signup)
  monthlyPrice: number;
  yearlyPrice: number;
  applications: number;
  showButton: boolean;
  isOutlined?: boolean;
  hasFreeTrial?: boolean;
  features: PlanFeature[];
}

const BASE_FEATURES: PlanFeature[] = [
  { icon: CalendarMonth, label: "7 днів безкоштовно" },
  { icon: Telegram, label: "Повідомлення в Telegram" },
  { icon: HeadsetMic, label: "Технічна підтримка" },
  { icon: Groups, label: "Необмежена кількість співробітників" },
];

const ENTERPRISE_FEATURES: PlanFeature[] = [
  ...BASE_FEATURES.filter((feature) => feature.icon !== CalendarMonth),
  { icon: AutoAwesome, label: "Запити на новий функціонал" },
];

const PRICING_PLANS: PricingPlan[] = [
  {
    name: "Brandly START",
    planKey: "start",
    monthlyPrice: 599,
    yearlyPrice: 5990,
    applications: 30,
    showButton: true,
    hasFreeTrial: true,
    features: BASE_FEATURES,
  },
  {
    name: "Brandly PRO",
    planKey: "pro",
    monthlyPrice: 1000,
    yearlyPrice: 10000,
    applications: 70,
    showButton: true,
    isOutlined: true,
    hasFreeTrial: true,
    features: BASE_FEATURES,
  },
  {
    name: "Brandly PRO+",
    planKey: "pro_plus",
    monthlyPrice: 2500,
    yearlyPrice: 25000,
    applications: 300,
    showButton: true,
    hasFreeTrial: true,
    features: BASE_FEATURES,
  },
  {
    name: "Brandly ENTERPRISE",
    planKey: null,
    monthlyPrice: 5000,
    yearlyPrice: 50000,
    applications: 2000,
    showButton: true,
    features: ENTERPRISE_FEATURES,
  },
];

const BILLING_OPTIONS: [
  { value: CompanyPaymentPeriodType; label: string },
  { value: CompanyPaymentPeriodType; label: string },
] = [
  { value: CompanyPaymentPeriod.MONTHLY, label: "в місяць" },
  { value: CompanyPaymentPeriod.YEARLY, label: "в рік" },
];

export const Pricing = () => {
  const navigate = useNavigate();
  const { setPlanAndPeriod } = useSignupPlanStore();
  const [selectedPeriod, setSelectedPeriod] =
    useState<CompanyPaymentPeriodType>(CompanyPaymentPeriod.MONTHLY);

  const handleSelectPlan = (planKey: SignupAllowedPlan) => {
    setPlanAndPeriod(planKey, selectedPeriod);

    const params = new URLSearchParams({
      plan: planKey,
      period: selectedPeriod,
    });

    void navigate(`${RoutePaths.SIGN_UP}?${params.toString()}`);
  };

  return (
    <LandingLayout>
      <LandingSection>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: { xs: 4, sm: 5, md: 6, lg: 8 },
          }}
        >
          {/* Header */}
          <FadeIn>
            <Box
              sx={{
                textAlign: "center",
                px: { xs: 2, sm: 3, md: 4, lg: 8 },
              }}
            >
              <Typography
                level="h1"
                sx={{
                  fontSize: {
                    xs: "2rem",
                    sm: "2.5rem",
                    md: "3rem",
                    lg: "4.25rem",
                  },
                  fontWeight: 600,
                  lineHeight: 1.15,
                  letterSpacing: "-0.02em",
                  mb: { xs: 1.5, md: 2 },
                }}
              >
                Почніть користуватися Brandly{" "}
                <Box component="span" sx={{ color: "primary.500" }}>
                  безкоштовно
                </Box>
              </Typography>
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: { xs: "14px", sm: "15px", md: "16px" },
                  lineHeight: "175%",
                  letterSpacing: "0.15px",
                  color: "text.secondary",
                }}
              >
                Позбудьтеся хаосу в реєстрі й пропущених дедлайнів — працюйте
                впевнено щодня.
              </Typography>
            </Box>
          </FadeIn>

          {/* Pricing Cards */}
          <Box>
            <FadeIn delay={100}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 2, md: 3 },
                  flexDirection: { xs: "column", sm: "row" },
                  alignSelf: { xs: "center", sm: "flex-start" },
                  mb: { xs: 2, sm: 2 },
                }}
              >
                <ToggleSwitch
                  options={BILLING_OPTIONS}
                  value={selectedPeriod}
                  onChange={(period) => setSelectedPeriod(period)}
                />

                <Typography
                  sx={{
                    fontSize: { xs: "14px", sm: "15px", md: "16px" },
                    fontWeight: 500,
                    color: "primary.500",
                  }}
                >
                  Заощаджуйте до 20% з річною підпискою
                </Typography>
              </Box>
            </FadeIn>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  lg: "repeat(4, 1fr)",
                },
                gap: { xs: 2, sm: 2.5, md: 3 },
                width: "100%",
              }}
            >
              {PRICING_PLANS.map((plan, index) => (
                <FadeIn key={plan.name} delay={150 + index * 100}>
                  <Card
                    variant="outlined"
                    sx={{
                      p: { xs: 2.5, sm: 3, md: 3.5 },
                      display: "flex",
                      flexDirection: "column",
                      gap: { xs: 2, md: 2.5 },
                      bgcolor: "background.surface",
                      borderColor: plan.isOutlined ? "primary.500" : "divider",
                      borderWidth: plan.isOutlined ? 2 : 1,
                      transition: "all 0.2s",
                      height: "100%",
                      position: "relative",
                      ...(plan.isOutlined && {
                        boxShadow: "0 0 0 1px var(--joy-palette-primary-500)",
                      }),
                    }}
                  >
                    {/* Popular Tag */}
                    {plan.isOutlined && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: -1,
                          right: { xs: 12, sm: 16, md: 20 },
                          bgcolor: "primary.500",
                          color: "primary.50",
                          px: { xs: 1.5, sm: 2 },
                          py: { xs: 0.5, sm: 0.75 },
                          borderRadius: "0 0 8px 8px",
                          fontSize: { xs: "11px", sm: "12px", md: "13px" },
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Популярний
                      </Box>
                    )}

                    {/* Plan Name */}
                    <Typography
                      sx={{
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        color: "text.secondary",
                        fontWeight: 600,
                        fontSize: { xs: "14px", sm: "15px", md: "16px" },
                      }}
                    >
                      {plan.name}
                    </Typography>

                    <Divider />

                    {/* Price */}
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "baseline",
                          gap: 0.5,
                        }}
                      >
                        <ScramblePrice
                          price={
                            selectedPeriod === CompanyPaymentPeriod.YEARLY
                              ? plan.yearlyPrice
                              : plan.monthlyPrice
                          }
                        />
                      </Box>
                      <Typography
                        sx={{
                          color: "text.tertiary",
                          mt: 0.5,
                          fontSize: { xs: "12px", sm: "13px", md: "14px" },
                        }}
                      >
                        {selectedPeriod === CompanyPaymentPeriod.YEARLY
                          ? "на рік"
                          : "на місяць"}
                      </Typography>
                    </Box>

                    {/* Features */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        flex: 1,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Description
                          sx={{
                            fontSize: { xs: 18, md: 20 },
                            color: "primary.500",
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: { xs: "13px", sm: "14px", md: "15px" },
                          }}
                        >
                          {plan.applications} заявок
                        </Typography>
                      </Box>
                      {plan.features.map((feature) => (
                        <Box
                          key={feature.label}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <feature.icon
                            sx={{
                              fontSize: { xs: 18, md: 20 },
                              color: "primary.500",
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: {
                                xs: "13px",
                                sm: "14px",
                                md: "15px",
                              },
                            }}
                          >
                            {feature.label}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    {/* Button */}
                    {plan.showButton && plan.planKey && (
                      <Button
                        variant="solid"
                        color="primary"
                        size="lg"
                        fullWidth
                        onClick={() => handleSelectPlan(plan.planKey!)}
                        sx={{
                          mt: "20px",
                          py: { xs: 1.25, md: 1.5 },
                          fontSize: { xs: "sm", md: "md" },
                          fontWeight: 600,
                        }}
                      >
                        Розпочати
                      </Button>
                    )}
                    {plan.showButton && !plan.planKey && (
                      <Button
                        variant="outlined"
                        color="neutral"
                        size="lg"
                        fullWidth
                        onClick={() =>
                          navigate(`${RoutePaths.ABOUT_US}?section=contact`)
                        }
                        sx={{
                          mt: "20px",
                          py: { xs: 1.25, md: 1.5 },
                          fontSize: { xs: "sm", md: "md" },
                          fontWeight: 600,
                        }}
                      >
                        Звʼязатися з нами
                      </Button>
                    )}
                  </Card>
                </FadeIn>
              ))}
            </Box>
            <Typography
              sx={{
                textAlign: "center",
                fontSize: { xs: "11px", sm: "12px" },
                color: "neutral.400",
                mt: { xs: 1, md: 2 },
              }}
            >
              Або можете придбати безстроковий доступ як продукт, для цього
              зверніться в підтримку
            </Typography>
          </Box>
        </Box>
      </LandingSection>

      {/* FAQ Section */}
      <PricingFAQ />
    </LandingLayout>
  );
};
